/**
 * Site (building_group) address.
 *
 * A site can span several buildings, each with its own georeferenced address,
 * so a building's address is only ever edited at the building level (read-only
 * here, with a pointer to the building). Here we surface the
 * first building's address as the site's displayed address (children come back
 * sorted by name). If the site has a single building this is naturally that
 * building's address; if it has several differing ones, we show the first.
 *
 * When no building carries an address, we fall back to the site's own
 * `address_raw` / `address_iso20022` rather than claiming "no address". Imports
 * routinely land the ensemble's address on the site (it is what the RCP and the
 * RNIC sheet state) while leaving the buildings bare — showing "Aucune adresse"
 * over a site that plainly has one reads as data loss. That fallback address
 * (or the absence of any) is the site's own, so it is the only case where
 * `onEdit` is honoured: an override of a building-derived address would be
 * invisible.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAssetTypeByCode,
  useChildAssets,
  useAssetProperties,
  usePropertyDefinitionsByCodes,
  type Asset,
} from '../hooks/useQueries'
import { AddressCompactDisplay } from './AddressCompactDisplay'

interface SiteAddressDisplayProps {
  org: string
  siteUid: string
  /** Opens the address editor. Only used when the address is the site's own. */
  onEdit?: () => void
}

const ADDRESS_CODES = ['address_iso20022', 'address_raw']

type PropertyValue = { property_uid?: string | null; value?: unknown }

/** Reads the address pair out of one asset's property list. */
function pickAddress(
  props: PropertyValue[],
  isoUid: string | undefined,
  rawUid: string | undefined,
) {
  const isoVal = props.find((p) => p.property_uid === isoUid && p.value)?.value
  const rawVal = props.find((p) => p.property_uid === rawUid && p.value)?.value
  return {
    iso: isoVal && typeof isoVal === 'object' ? (isoVal as Record<string, any>) : null,
    raw: typeof rawVal === 'string' ? rawVal : null,
  }
}

export function SiteAddressDisplay({ org, siteUid, onEdit }: SiteAddressDisplayProps) {
  const { t } = useTranslation('assets')
  const { data: buildingType } = useAssetTypeByCode(org, 'building')
  const { data: children = [] } = useChildAssets(org, siteUid)
  const { data: addrDefs = [] } = usePropertyDefinitionsByCodes(org, ADDRESS_CODES)

  const firstBuilding = useMemo<Asset | undefined>(() => {
    if (!buildingType?.uid) return undefined
    return (children as Asset[]).find((c) => c.asset_type_uid === buildingType.uid)
  }, [children, buildingType?.uid])

  const { data: buildingProps = [] } = useAssetProperties(org, firstBuilding?.uid || '')
  const { data: siteProps = [] } = useAssetProperties(org, siteUid)

  const { iso, raw, fromBuilding } = useMemo(() => {
    const defUid = (code: string) => addrDefs.find((d) => d.code === code)?.uid
    const isoUid = defUid('address_iso20022')
    const rawUid = defUid('address_raw')

    const own = pickAddress(buildingProps, isoUid, rawUid)
    if (own.iso || own.raw) return { ...own, fromBuilding: true }

    return { ...pickAddress(siteProps, isoUid, rawUid), fromBuilding: false }
  }, [buildingProps, siteProps, addrDefs])

  if (fromBuilding) {
    // Read-only: the address is owned by the building.
    return (
      <div data-testid="site-address-from-building">
        <AddressCompactDisplay addressIso20022={iso} addressRaw={raw} />
        {firstBuilding?.name && (
          <p className="px-2 text-xs text-muted-foreground" data-testid="site-address-building-hint">
            {t('detail.address_from_building', { name: firstBuilding.name })}
          </p>
        )}
      </div>
    )
  }
  return <AddressCompactDisplay addressIso20022={iso} addressRaw={raw} onEdit={onEdit} />
}
